import React from 'react';
const { Icon, Logo, Button, StatusPill, Card, Avatar, BarMini, Donut, Tabs, IndiceBadge } = window;
/* ============================================================
   BOOKER — Tela: Central de alertas e renovações
   ============================================================ */
var { useState, useEffect, useRef, useMemo } = React;

function Alertas({ onNavigate, sync }) {
  const DB = window.DB;
  const { fmtBRL, fmtData, calc } = DB;
  const todos = useMemo(() => DB.contratosComCliente(), [sync]);
  const [filtro, setFiltro] = useState('all');
  const [feitos, setFeitos] = useState({});
  const [fMes, setFMes] = useState('all');
  const [confirmando, setConfirmando] = useState({});
  const podeEditar = window.podeEditar ? window.podeEditar() : true;

  async function confirmarValor(k) {
    setConfirmando(c => ({ ...c, [k.id]: true }));
    try {
      await window.Api.apiConfirmarValor(k.dbId);
      if (window.recarregarContratos) await window.recarregarContratos();
    } catch (e) { window.avisar(e.message || 'Falha ao confirmar valor.'); }
    finally { setConfirmando(c => ({ ...c, [k.id]: false })); }
  }

  // Nova realidade: renovação anual automática. Alertas = renovações próximas
  // (informativo) + valores a revisar (valor inicial zerado/baixo demais, que
  // inviabiliza a correção pelo índice). Encerrados não geram alerta.
  const alertas = useMemo(() => {
    const arr = [];
    todos.filter(k => k.status !== 'Encerrado').forEach(k => {
      const vi = k.valorInicial;
      if (vi == null || vi <= 0)
        arr.push({ id: k.id + '-val0', tipo: 'Valor', prio: 4, k, titulo: 'Valor inicial não informado', desc: 'Correção pelo índice indisponível — informe o valor inicial do contrato', icon: 'alert', color: 'var(--danger)' });
      else if (vi < 100 && !k.valorConferido)
        arr.push({ id: k.id + '-valb', tipo: 'Valor', baixo: true, prio: 4, k, titulo: `Valor inicial muito baixo · ${fmtBRL(vi)}`, desc: 'Valor suspeito para correção — confirme se está certo ou corrija a extração', icon: 'alert', color: 'var(--danger)' });
      if (k.dias >= 0 && k.dias <= 30)
        arr.push({ id: k.id + '-r30', tipo: 'Renovação', prio: 3, k, titulo: `Renova em ${k.dias} dias`, desc: `Renovação automática em ${fmtData(k.dataVencimento)} — janela de 30 dias`, icon: 'clock', color: 'var(--booker-orange)' });
    });
    return arr.sort((a, b) => b.prio - a.prio);
  }, [todos]);

  // Filtro por mês de vencimento
  const MES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const mesKey = (d) => { const x = DB.parseData(d); return isNaN(x.getTime()) ? '' : `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`; };
  const mesesDisp = useMemo(() => {
    const m = new Map();
    alertas.forEach(a => { const x = DB.parseData(a.k.dataVencimento); if (!isNaN(x.getTime())) m.set(mesKey(a.k.dataVencimento), `${MES[x.getMonth()]}/${x.getFullYear()}`); });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [alertas]);

  const noMes = useMemo(() => (fMes === 'all' ? alertas : alertas.filter(a => mesKey(a.k.dataVencimento) === fMes)), [alertas, fMes]);
  const counts = {
    all: noMes.length,
    Renovação: noMes.filter(a => a.tipo === 'Renovação').length,
    Valor: noMes.filter(a => a.tipo === 'Valor').length,
  };
  const lista = noMes.filter(a => filtro === 'all' || a.tipo === filtro).filter(a => !feitos[a.id]);
  // correção pelo índice acumulada (consultiva) — só carteira ativa; obedece o
  // filtro de mês de vencimento (em "todos", soma a carteira ativa inteira).
  const totalCorrecao = todos
    .filter(k => k.status !== 'Encerrado' && (fMes === 'all' || mesKey(k.dataVencimento) === fMes))
    .reduce((s, k) => s + Math.max(0, calc.correcaoAutomatica(k).divergencia), 0);

  const Chip = ({ id, label, n, color }) => (
    <button onClick={() => setFiltro(id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 15px', borderRadius: 'var(--r-sm)', fontWeight: 700, fontSize: 13.5,
      border: '1px solid ' + (filtro === id ? 'transparent' : 'var(--border-strong)'),
      background: filtro === id ? 'var(--primary-btn)' : 'var(--surface)',
      color: filtro === id ? 'var(--primary-btn-text, #fff)' : 'var(--text)' }}>
      {label}<span style={{ background: filtro === id ? 'rgba(255,255,255,.2)' : (color || 'var(--surface-inset)'), color: filtro === id ? '#fff' : (color ? '#fff' : 'var(--text-muted)'),
        fontSize: 11.5, padding: '1px 7px', borderRadius: 99 }}>{n}</span>
    </button>
  );
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

  return (
    <div style={{ animation: 'fadeUp .4s ease both' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-.02em' }}>Central de alertas</h1>
        <p style={{ margin: '5px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>{alertas.length} alertas ativos · renovações automáticas e valores a revisar</p>
      </div>

      {/* Resumo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 20 }}>
        <Card pad={18} style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <span style={{ width: 46, height: 46, borderRadius: 12, background: 'var(--warn-bg)', color: 'var(--booker-orange)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="clock" size={22} /></span>
          <div><div className="tnum" style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-strong)' }}>{counts.Renovação}</div><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Renovações · próximos 30 dias</div></div>
        </Card>
        <Card pad={18} style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <span style={{ width: 46, height: 46, borderRadius: 12, background: 'var(--danger-bg)', color: 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="alert" size={22} /></span>
          <div><div className="tnum" style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-strong)' }}>{counts.Valor}</div><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Valores a revisar</div></div>
        </Card>
        <Card pad={18} style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <span style={{ width: 46, height: 46, borderRadius: 12, background: 'var(--ok-bg)', color: 'var(--ok)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="trend" size={22} /></span>
          <div><div className="tnum" style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-strong)' }}>{fmtBRL(totalCorrecao)}</div><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Correção pelo índice /mês · consultivo</div></div>
        </Card>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip id="all" label="Todos" n={counts.all} />
        <Chip id="Renovação" label="Renovações" n={counts.Renovação} color="var(--warn)" />
        <Chip id="Valor" label="Valores a revisar" n={counts.Valor} color="var(--danger)" />
        <div style={{ flex: 1 }} />
        <div style={{ position: 'relative' }}>
          <select value={fMes} onChange={e => setFMes(e.target.value)} style={{ appearance: 'none', padding: '9px 34px 9px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-strong)', background: 'var(--surface)', color: 'var(--text-strong)', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' }}>
            <option value="all">Mês de vencimento: todos</option>
            {mesesDisp.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <Icon name="chevronD" size={14} style={{ position: 'absolute', right: 11, top: 11, pointerEvents: 'none', color: 'var(--text-muted)' }} />
        </div>
      </div>

      {/* Lista */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {lista.map((a, i) => (
          <Card key={a.id} pad={0} style={{ animation: `fadeUp .35s ${i * 0.03}s both`, borderLeft: `4px solid ${a.color}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px' }}>
              <span style={{ width: 44, height: 44, borderRadius: 12, background: a.color + '1e', color: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={a.icon} size={21} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>{a.titulo}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: a.color, background: a.color + '1e', padding: '2px 8px', borderRadius: 99 }}>{a.tipo}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>
                  <b style={{ color: 'var(--text)' }}>{a.k.cliente.fantasia}</b> · <span className="mono">{a.k.codigo}</span> · {a.desc}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <Button size="sm" variant="ghost" onClick={() => onNavigate('detalhe', a.k.id)}>Abrir</Button>
                {a.baixo && podeEditar
                  ? <Button size="sm" variant="accent" icon="check" onClick={confirmando[a.k.id] ? undefined : () => confirmarValor(a.k)}>{confirmando[a.k.id] ? 'Confirmando…' : 'Confirmar valor'}</Button>
                  : <Button size="sm" variant="default" icon="check" onClick={() => setFeitos(f => ({ ...f, [a.id]: true }))}>Resolver</Button>}
              </div>
            </div>
          </Card>
        ))}
        {!lista.length && (
          <Card style={{ textAlign: 'center', padding: 50 }}>
            <div style={{ width: 56, height: 56, borderRadius: 99, background: 'var(--ok-bg)', color: 'var(--ok)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Icon name="check" size={28} /></div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-strong)' }}>Tudo resolvido!</div>
            <p style={{ fontSize: 13.5, color: 'var(--text-muted)', margin: '6px 0 0' }}>Nenhum alerta pendente neste filtro.</p>
          </Card>
        )}
      </div>
    </div>
  );
}

Object.assign(window, { Alertas });
